import { combineReducers } from 'redux';
import { call, put, takeLatest } from 'redux-saga/effects';

import { api } from './api';
import { environment } from 'environments/environment';
import { ExtendedAxiosResponse } from 'helpers/apiClient';
import {
  createActionType,
  createApiActionCreators,
  createReducer,
  RequestActionTypes,
  AppAction,
  LoadingStatus,
  createLoadingStateReducer,
  createActionCreator,
} from 'helpers/redux/reduxHelpers';
import { AppState } from 'helpers/store/models/AppState';
import { ApiClientRequest } from 'models/ApiClientRequest';
import { GetDetailRecordsPayload, filterPayload } from 'models/GetDetailRecordsPayload';
import { DetailRecordRowDTO, DetailRecordViewVO } from 'generated/bslEbill/detailRecordView/models';

import { getApiUrl, performApiCall } from 'saga/apiClient/ducks';

/* STATE */
export interface DetailRecordState {
  data: DetailRecordViewVO;
  lazyload: DetailRecordViewVO;
  loadingStatus: LoadingStatus;
  detailRecordRows: Array<DetailRecordRowDTO>;
}

/* ACTION TYPES */
export enum DetailRecordActionTypes {
  GetData = '@@DetailRecord/GET_DATA',
  Lazyload = '@@DetailRecord/LAZY_LOAD',
  SetLoading = '@@DetailRecord/SET_LOADING',
  ResetData = '@@DetailRecord/RESET_DATA',
}

/* ACTIONS */
export const getDataAction = createApiActionCreators(DetailRecordActionTypes.GetData);
export const lazyloadAction = createApiActionCreators(DetailRecordActionTypes.Lazyload);
export const setLoadingAction = createActionCreator(DetailRecordActionTypes.SetLoading);
export const resetDataAction = createActionCreator(DetailRecordActionTypes.ResetData);

/* REDUCERS */
const initialState: DetailRecordState = {
  data: {},
  lazyload: {},
  loadingStatus: LoadingStatus.loading,
  detailRecordRows: [],
};
const data = createReducer(initialState.data, {
  [DetailRecordActionTypes.GetData]: {
    [RequestActionTypes.SUCCESS]: (_state: DetailRecordViewVO | undefined, payload: DetailRecordViewVO) => payload,
    [RequestActionTypes.FAILURE]: () => {
      return {};
    },
  },
});

const lazyload = createReducer(initialState.lazyload, {
  [DetailRecordActionTypes.Lazyload]: {
    [RequestActionTypes.SUCCESS]: (_state: DetailRecordViewVO, payload: DetailRecordViewVO) => payload,
    [RequestActionTypes.FAILURE]: () => {
      undefined;
    },
  },
});

const loadingStatus = createLoadingStateReducer(
  initialState.loadingStatus,
  {
    [DetailRecordActionTypes.Lazyload]: [
      RequestActionTypes.REQUEST,
      RequestActionTypes.SUCCESS,
      RequestActionTypes.FAILURE,
    ],
  },
  {
    [DetailRecordActionTypes.SetLoading]: (state: LoadingStatus, status: LoadingStatus) => status,
  }
);

const detailRecordRows = createReducer(initialState.detailRecordRows, {
  [DetailRecordActionTypes.GetData]: {
    [RequestActionTypes.SUCCESS]: (_state: Array<DetailRecordRowDTO>, payload: DetailRecordViewVO) => [
      ..._state,
      ...(payload.detailRecordRows || []),
    ],
    [RequestActionTypes.FAILURE]: () => [],
  },
  [DetailRecordActionTypes.Lazyload]: {
    [RequestActionTypes.SUCCESS]: (_state: Array<DetailRecordRowDTO>, payload: DetailRecordViewVO) => [
      ..._state,
      ...(payload.detailRecordRows || []),
    ],
    [RequestActionTypes.FAILURE]: () => [],
  },
  [DetailRecordActionTypes.ResetData]: () => [],
});

export default combineReducers<DetailRecordState>({
  data,
  lazyload,
  loadingStatus,
  detailRecordRows,
});

/* SELECTORS */
export const selectDetailRecord = (state: AppState) => state.detailRecordView;
export const selectGetData = (state: AppState): DetailRecordViewVO => selectDetailRecord(state).data;
export const selectLazyload = (state: AppState): DetailRecordViewVO => selectDetailRecord(state).lazyload;
export const selectLoading = (state: AppState): LoadingStatus => selectDetailRecord(state).loadingStatus;
export const selectDetailRecordRows = (state: AppState): Array<DetailRecordRowDTO> =>
  selectDetailRecord(state).detailRecordRows;

/* SAGAS */
export function* getData({ payload }: AppAction<GetDetailRecordsPayload>) {
  payload = filterPayload(payload);
  yield put(setLoadingAction(LoadingStatus.loading));

  const request: ApiClientRequest = {
    apiCall: call(
      api.getData,
      yield getApiUrl(environment.urlConfig.billingManagement),
      payload.invoiceNumber,
      payload.serviceNo,
      payload.serviceId,
      payload.ebillSection,
      payload.ebillGroup,
      payload.ebillOrigNo,
      payload.ebillTermNo,
      payload.ebillOrderBy
    ),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    yield put(getDataAction.success(resp.data));
    yield put(setLoadingAction(LoadingStatus.success));
  } else {
    yield put(getDataAction.failure());
    yield put(setLoadingAction(LoadingStatus.failure));
  }
}

export function* getLazyload({ payload }: AppAction<GetDetailRecordsPayload>) {
  payload = filterPayload(payload);
  yield put(setLoadingAction(LoadingStatus.loading));

  const request: ApiClientRequest = {
    apiCall: call(
      api.lazyload,
      yield getApiUrl(environment.urlConfig.billingManagement),
      payload.invoiceNumber,
      payload.serviceNo,
      payload.serviceId,
      payload.ebillSection,
      payload.ebillGroup,
      payload.ebillOrigNo,
      payload.ebillTermNo,
      payload.ebillOrderBy,
      payload.summaryGroupCode,
      payload.summaryChargeCode,
      payload.posNext,
      payload.pageSize
    ),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    yield put(lazyloadAction.success(resp.data));
    yield put(setLoadingAction(LoadingStatus.success));
  } else {
    yield put(lazyloadAction.failure());
    yield put(setLoadingAction(LoadingStatus.failure));
  }
}

/* EXPORT */
export function* detailRecordViewSaga() {
  yield takeLatest(createActionType(DetailRecordActionTypes.GetData, RequestActionTypes.REQUEST), getData);

  yield takeLatest(createActionType(DetailRecordActionTypes.Lazyload, RequestActionTypes.REQUEST), getLazyload);
}
